import { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation, Link as RouterLink } from "react-router-dom";
import {
  ArrowLeft, Plus, X, Check, Loader2, AlertCircle, ExternalLink, Copy,
  ChevronUp, ChevronDown, Trash2, Link2, Type, Image as ImageIcon, Youtube,
  GripVertical, SquareStack, Palette, Eye, User as UserIcon, Sparkles, Share2, Rocket,
  Heading, Minus, Star, CalendarClock, FormInput, MousePointerClick, Send,
} from "lucide-react";
import {
  BlockType, LandingPage, PageBlock, PageDesign, PageSocial, GhlCustomField,
} from "../../../types";
import { useAppServices } from "../../../hook/services";
import LeadFieldsEditor from "../../../components/LeadFieldsEditor";
import HelpHint from "../../../components/ui/HelpHint";
import { useMarketplace } from "../../../marketplace/context";
import { buildPageUrl, copyToClipboard, toLocalInputValue } from "../../../utils";
import env from "../../../config";
import Spinner from "../../../components/ui/Spinner";
import AddBlockModal from "./AddBlockModal";
import PagePreview, { FONT_STACK } from "./PagePreview";
import { SocialGlyph, SOCIAL_PLATFORMS } from "./socialIcons";

const appHost = env.REDIRECT_BASE.replace(/^https?:\/\//, "");
const COLORS = ["#6366f1", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#0ea5e9", "#ec4899", "#0f172a"];
const FONTS = ["Inter", "Poppins", "Open Sans", "Roboto", "Montserrat", "Playfair Display"];
const FONT_G: Record<string, string> = {
  Inter: "Inter:wght@400;600;700", Poppins: "Poppins:wght@400;600;700",
  "Open Sans": "Open+Sans:wght@400;600;700", Roboto: "Roboto:wght@400;700",
  Montserrat: "Montserrat:wght@400;600;700", "Playfair Display": "Playfair+Display:wght@600;700",
};
const DEFAULT_DESIGN: PageDesign = {
  theme: "light", bg_type: "gradient", bg_color: "#f7f8fc",
  bg_gradient_from: "#eef2ff", bg_gradient_to: "#fdf4ff", text_color: "#18181b",
  font: "Inter", button_shape: "rounded", button_shadow: "soft",
  button_color: "#6366f1", button_text_color: "#ffffff", social_placement: "top",
};

const THEMES: { key: string; swatch: string; patch: Partial<PageDesign> }[] = [
  { key: "light", swatch: "#ffffff", patch: { bg_type: "color", bg_color: "#ffffff", text_color: "#18181b", button_color: "#6366f1", button_text_color: "#ffffff" } },
  { key: "sky", swatch: "#0ea5e9", patch: { bg_type: "gradient", bg_gradient_from: "#e0f2fe", bg_gradient_to: "#eff6ff", text_color: "#0c4a6e", button_color: "#0ea5e9", button_text_color: "#ffffff" } },
  { key: "mint", swatch: "#10b981", patch: { bg_type: "gradient", bg_gradient_from: "#d1fae5", bg_gradient_to: "#ecfdf5", text_color: "#064e3b", button_color: "#10b981", button_text_color: "#ffffff" } },
  { key: "sunset", swatch: "#f59e0b", patch: { bg_type: "gradient", bg_gradient_from: "#ffedd5", bg_gradient_to: "#fff7ed", text_color: "#7c2d12", button_color: "#f59e0b", button_text_color: "#ffffff" } },
  { key: "rose", swatch: "#f43f5e", patch: { bg_type: "gradient", bg_gradient_from: "#ffe4e6", bg_gradient_to: "#fff1f2", text_color: "#881337", button_color: "#f43f5e", button_text_color: "#ffffff" } },
  { key: "indigo", swatch: "#6366f1", patch: { bg_type: "gradient", bg_gradient_from: "#e0e7ff", bg_gradient_to: "#eef2ff", text_color: "#312e81", button_color: "#6366f1", button_text_color: "#ffffff" } },
  { key: "dark", swatch: "#111827", patch: { bg_type: "color", bg_color: "#111827", text_color: "#f9fafb", button_color: "#6366f1", button_text_color: "#ffffff" } },
];

const BLOCK_META: Record<BlockType, { label: string; icon: any; color: string }> = {
  link: { label: "Link", icon: Link2, color: "#0ea5e9" },
  header: { label: "Section header", icon: Heading, color: "#0d9488" },
  text: { label: "Text", icon: Type, color: "#ec4899" },
  divider: { label: "Divider", icon: Minus, color: "#64748b" },
  image: { label: "Image", icon: ImageIcon, color: "#6366f1" },
  youtube: { label: "YouTube", icon: Youtube, color: "#ef4444" },
  form: { label: "Form", icon: FormInput, color: "#7c3aed" },
};

// Stable per-block id for click/submission tracking (browser-side).
const newBlockId = () => "b" + Math.random().toString(36).slice(2, 10);

const Builder = () => {
  const { id } = useParams<{ id: string }>();
  const editing = !!id;
  const navigate = useNavigate();
  const location = useLocation();
  const services = useAppServices();
  const { buildPath } = useMarketplace();
  const tpl: any = (!editing && (location.state as any)?.template) || null;

  const [loading, setLoading] = useState(editing);
  const [tab, setTab] = useState<"build" | "design">("build");
  const [addOpen, setAddOpen] = useState(false);

  const [title, setTitle] = useState(tpl?.title || "");
  const [slug, setSlug] = useState("");
  const [origSlug, setOrigSlug] = useState("");
  const [slugStatus, setSlugStatus] = useState<{ state: "idle" | "checking" | "ok" | "bad"; reason?: string }>({ state: "idle" });
  const [bio, setBio] = useState(tpl?.bio || "");
  const [avatarImage, setAvatarImage] = useState(tpl?.avatar_image || "");
  const [avatarColor, setAvatarColor] = useState(tpl?.avatar_color || COLORS[0]);
  const [blocks, setBlocks] = useState<PageBlock[]>(tpl?.blocks || []);
  const [socials, setSocials] = useState<PageSocial[]>(tpl?.socials || []);
  const [design, setDesign] = useState<PageDesign>(tpl?.design ? { ...DEFAULT_DESIGN, ...tpl.design } : DEFAULT_DESIGN);
  const [published, setPublished] = useState(true);

  const [submitting, setSubmitting] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [customFields, setCustomFields] = useState<GhlCustomField[]>([]);

  // GHL custom fields for the form-block field picker.
  useEffect(() => {
    let cancelled = false;
    services.ghl.custom_fields().then(({ response }) => {
      if (!cancelled && Array.isArray(response)) setCustomFields(response);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!editing) return;
    (async () => {
      const { response } = await services.landing_pages.filter({ query: `_id=${id}` });
      if (response) {
        const p = response as LandingPage;
        setTitle(p.title || "");
        setSlug(p.slug || "");
        setOrigSlug(p.slug || "");
        setBio(p.bio || "");
        setAvatarImage(p.avatar_image || "");
        setAvatarColor(p.avatar_color || COLORS[0]);
        setBlocks(p.blocks?.length ? p.blocks : []);
        setSocials(p.socials || []);
        setDesign({ ...DEFAULT_DESIGN, ...(p.design || {}) });
        setPublished(p.published !== false);
      }
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing, id]);

  useEffect(() => {
    const g = FONT_G[design.font];
    if (!g) return;
    const elId = `gfont-${design.font}`;
    if (document.getElementById(elId)) return;
    const link = document.createElement("link");
    link.id = elId;
    link.rel = "stylesheet";
    link.href = `https://fonts.googleapis.com/css2?family=${g}&display=swap`;
    document.head.appendChild(link);
  }, [design.font]);

  useEffect(() => {
    const s = slug.trim().toLowerCase();
    if (!s || s === origSlug) return setSlugStatus({ state: "idle" });
    setSlugStatus({ state: "checking" });
    const t = setTimeout(async () => {
      const q = `slug=${encodeURIComponent(s)}${id ? `&_id=${id}` : ""}`;
      const { response, error } = await services.landing_pages.slugAvailable({ query: q });
      if (error || !response) return setSlugStatus({ state: "idle" });
      setSlugStatus(response.available ? { state: "ok" } : { state: "bad", reason: response.reason || "That URL is already taken." });
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, origSlug, id]);

  const addBlock = (type: BlockType) => {
    const base: PageBlock = {
      id: newBlockId(),
      type,
      label: "",
      url: "",
      text: "",
      image_url: "",
      description: "",
    };
    if (type === "form") {
      base.label = "Get in touch";
      base.fields = [{ key: "name" }, { key: "email", required: true }];
      base.button_label = "Submit";
      base.success_message = "Thanks! We'll be in touch.";
    }
    setBlocks((b) => [...b, base]);
    setAddOpen(false);
  };
  const updateBlock = (i: number, patch: Partial<PageBlock>) =>
    setBlocks((b) => b.map((x, idx) => (idx === i ? { ...x, ...patch } : x)));
  const removeBlock = (i: number) => setBlocks((b) => b.filter((_, idx) => idx !== i));
  const moveBlock = (i: number, dir: -1 | 1) =>
    setBlocks((b) => {
      const j = i + dir;
      if (j < 0 || j >= b.length) return b;
      const copy = [...b];
      [copy[i], copy[j]] = [copy[j], copy[i]];
      return copy;
    });
  const setD = (patch: Partial<PageDesign>) => setDesign((d) => ({ ...d, ...patch }));

  const copyUrl = async () => {
    if (await copyToClipboard(buildPageUrl(slug))) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  };

  const handleSave = async () => {
    setError(null);
    if (!title.trim()) { setTab("build"); return setError("Add a page title."); }
    if (slug.trim().length < 3) { setTab("build"); return setError("URL must be at least 3 characters."); }
    setSubmitting(true);
    const payload: any = {
      title: title.trim(), slug: slug.trim().toLowerCase(), bio: bio.trim(),
      avatar_image: avatarImage.trim(), avatar_color: avatarColor,
      blocks, socials: socials.filter((s) => s.url.trim()), design, published,
    };
    const res = editing
      ? await services.landing_pages.update({ payload: { _id: id, ...payload } })
      : await services.landing_pages.create({ payload });
    setSubmitting(false);
    if (res.error) return setError(res.error.message || res.error.error || "Failed to save page");
    if (!editing && res.response?._id) navigate(buildPath(`landing-pages/${res.response._id}`), { replace: true });
    else setOrigSlug(payload.slug);
    setSaved(true);
    setTimeout(() => setSaved(false), 1500);
  };

  if (loading) {
    return <div className="min-h-[40vh] flex items-center justify-center"><Spinner size={26} /></div>;
  }

  return (
    <div className="space-y-5">
      {/* Toolbar — sticks to the top so Publish stays reachable while scrolling */}
      <div className="sticky top-0 z-20 bg-white/90 backdrop-blur-xl border border-ink-200/70 rounded-2xl shadow-card px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <RouterLink to={buildPath("landing-pages")} className="w-8 h-8 rounded-lg border border-ink-200 text-ink-500 hover:text-ink-900 hover:bg-ink-50 flex items-center justify-center shrink-0">
            <ArrowLeft size={15} />
          </RouterLink>
          <div className="min-w-0">
            <div className="text-sm font-semibold text-ink-900 truncate">{title || "Untitled page"}</div>
            <button onClick={copyUrl} className="inline-flex items-center gap-1.5 text-xs text-brand-600 hover:text-brand-700 font-mono">
              {copied ? <Check size={11} /> : <Copy size={11} />} {appHost}/p/{slug || "…"}
            </button>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            role="switch"
            aria-checked={published}
            onClick={() => setPublished((p) => !p)}
            title={published ? "Live — visible to anyone with the link" : "Draft — hidden from the public"}
            className="inline-flex items-center gap-2 h-9 pl-1.5 pr-3 rounded-lg hover:bg-ink-50 transition-colors"
          >
            <span className={`relative w-9 h-5 rounded-full transition-colors ${published ? "bg-emerald-500" : "bg-ink-300"}`}>
              <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow-sm transition-transform ${published ? "translate-x-4" : ""}`} />
            </span>
            <span className={`text-xs font-semibold ${published ? "text-emerald-700" : "text-ink-500"}`}>
              {published ? "Published" : "Draft"}
            </span>
          </button>
          {editing && slug && (
            <a href={buildPageUrl(slug)} target="_blank" rel="noreferrer" className="w-9 h-9 rounded-lg border border-ink-200 text-ink-500 hover:text-ink-900 hover:bg-ink-50 flex items-center justify-center" title="Open public page">
              <ExternalLink size={15} />
            </a>
          )}
          <button
            onClick={handleSave}
            disabled={submitting}
            className="inline-flex items-center justify-center gap-1.5 h-9 px-4 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 shadow-[0_8px_20px_-8px_rgba(16,185,129,0.6)] disabled:opacity-60 transition-all active:scale-[0.98]"
          >
            {submitting ? (
              <Loader2 size={15} className="animate-spin" />
            ) : saved ? (
              <Check size={15} strokeWidth={2.5} />
            ) : (
              <Rocket size={15} strokeWidth={2.25} />
            )}
            {saved ? "Saved" : editing ? "Publish changes" : "Create page"}
          </button>
        </div>
      </div>

      {error && <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2.5">{error}</div>}

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Editor */}
        <div className="lg:col-span-3 space-y-4">
          {/* Tabs */}
          <div className="inline-flex p-1 bg-ink-100 rounded-xl">
            {([["build", "Build", SquareStack], ["design", "Design", Palette]] as const).map(([t, label, Icon]) => (
              <button
                key={t}
                onClick={() => setTab(t)}
                className={`inline-flex items-center gap-1.5 h-9 px-4 rounded-lg text-sm font-medium transition-all ${
                  tab === t ? "bg-white text-ink-900 shadow-sm" : "text-ink-500 hover:text-ink-900"
                }`}
              >
                <Icon size={15} /> {label}
              </button>
            ))}
          </div>

          {tab === "build" ? (
            <>
              {/* Profile */}
              <Card icon={<UserIcon size={14} />} title="Profile" subtitle="Avatar, title and bio shown at the top">
                <div className="flex items-start gap-4">
                  <div className="shrink-0 text-center">
                    {avatarImage ? (
                      <img src={avatarImage} alt="" className="w-16 h-16 rounded-full object-cover ring-2 ring-ink-100" />
                    ) : (
                      <div className="w-16 h-16 rounded-full flex items-center justify-center text-white font-display font-bold text-2xl ring-2 ring-ink-100" style={{ background: `linear-gradient(135deg, ${avatarColor}, ${avatarColor}cc)` }}>
                        {(title || "?").charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="text-[10px] text-ink-400 mt-1">Avatar</div>
                  </div>
                  <div className="flex-1 min-w-0 space-y-3">
                    <Field label="Page title">
                      <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Acme Inc." className={inputCls} />
                    </Field>
                    <Field label="Bio">
                      <textarea value={bio} onChange={(e) => setBio(e.target.value)} rows={2} placeholder="A short description" className={`${inputCls} h-auto py-2 resize-none`} />
                    </Field>
                  </div>
                </div>
                <Field label="Public URL">
                  <div className="flex items-center rounded-lg border border-ink-200 bg-white overflow-hidden focus-within:ring-2 focus-within:ring-brand-500/20 focus-within:border-brand-500">
                    <span className="pl-3 pr-1 text-xs text-ink-400 font-mono shrink-0">{appHost}/p/</span>
                    <input value={slug} onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, ""))} placeholder="acme" className="flex-1 pr-2 h-10 bg-transparent text-sm focus:outline-none font-mono" />
                    <span className="pr-2.5 shrink-0">
                      {slugStatus.state === "checking" && <Loader2 size={14} className="animate-spin text-ink-400" />}
                      {slugStatus.state === "ok" && <Check size={15} className="text-emerald-600" />}
                      {slugStatus.state === "bad" && <AlertCircle size={14} className="text-rose-500" />}
                    </span>
                  </div>
                  {slugStatus.state === "bad" && slugStatus.reason && <p className="text-[11px] text-rose-600 mt-1">{slugStatus.reason}</p>}
                </Field>
                <Field label="Avatar image URL (optional)">
                  <input value={avatarImage} onChange={(e) => setAvatarImage(e.target.value)} placeholder="https://…/photo.jpg" className={`${inputCls} font-mono`} />
                  {!avatarImage && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {COLORS.map((c) => (
                        <button key={c} type="button" onClick={() => setAvatarColor(c)} className={`w-7 h-7 rounded-lg ring-2 transition-transform ${avatarColor === c ? "ring-ink-900 scale-110" : "ring-transparent hover:scale-105"}`} style={{ background: c }} />
                      ))}
                    </div>
                  )}
                </Field>
              </Card>

              {/* Blocks */}
              <div>
                <div className="flex items-center justify-between mb-2 px-1">
                  <h3 className="text-sm font-semibold text-ink-900">Content blocks</h3>
                  <span className="text-[11px] text-ink-400">{blocks.length} block{blocks.length === 1 ? "" : "s"}</span>
                </div>
                <div className="space-y-3">
                  {blocks.map((b, i) => {
                    const Meta = BLOCK_META[b.type];
                    const Icon = Meta.icon;
                    return (
                      <div key={b.id || i} className="bg-white border border-ink-200/70 rounded-2xl shadow-card overflow-hidden">
                        <div className="flex items-center gap-2 px-3 h-12 border-b border-ink-100 bg-ink-50/40">
                          <GripVertical size={15} className="text-ink-300" />
                          <span className="w-7 h-7 rounded-lg flex items-center justify-center text-white shrink-0" style={{ background: Meta.color }}>
                            <Icon size={14} />
                          </span>
                          <span className="text-sm font-semibold text-ink-700">{Meta.label}</span>
                          {editing && (b.type === "link" || b.type === "image") && !!b.clicks && (
                            <span className="inline-flex items-center gap-1 text-[11px] text-ink-500 bg-ink-100 px-1.5 py-0.5 rounded" title="Clicks">
                              <MousePointerClick size={11} /> {b.clicks}
                            </span>
                          )}
                          {editing && b.type === "form" && !!b.submissions && (
                            <span className="inline-flex items-center gap-1 text-[11px] text-ink-500 bg-ink-100 px-1.5 py-0.5 rounded" title="Submissions">
                              <Send size={11} /> {b.submissions}
                            </span>
                          )}
                          <span className="flex-1" />
                          <button onClick={() => moveBlock(i, -1)} disabled={i === 0} className="w-7 h-7 rounded-md text-ink-400 hover:bg-ink-100 disabled:opacity-30 flex items-center justify-center"><ChevronUp size={14} /></button>
                          <button onClick={() => moveBlock(i, 1)} disabled={i === blocks.length - 1} className="w-7 h-7 rounded-md text-ink-400 hover:bg-ink-100 disabled:opacity-30 flex items-center justify-center"><ChevronDown size={14} /></button>
                          <button onClick={() => removeBlock(i)} className="w-7 h-7 rounded-md text-ink-400 hover:bg-rose-50 hover:text-rose-600 flex items-center justify-center"><Trash2 size={14} /></button>
                        </div>
                        <div className="p-4"><BlockFields block={b} onChange={(p) => updateBlock(i, p)} customFields={customFields} /></div>
                      </div>
                    );
                  })}
                  <button onClick={() => setAddOpen(true)} className="w-full flex items-center justify-center gap-2 h-14 rounded-2xl border-2 border-dashed border-ink-200 text-ink-500 hover:border-brand-400 hover:text-brand-600 hover:bg-brand-50/30 transition-colors text-sm font-semibold">
                    <Plus size={16} strokeWidth={2.5} /> Add block
                  </button>
                </div>
              </div>
            </>
          ) : (
            <DesignControls design={design} setD={setD} socials={socials} setSocials={setSocials} />
          )}
        </div>

        {/* Phone preview */}
        <div className="lg:col-span-2">
          <div className="sticky top-4 lg:top-[88px]">
            <div className="flex items-center justify-center gap-1.5 text-[11px] uppercase tracking-wider text-ink-400 font-semibold mb-3">
              <Eye size={12} /> Live preview
            </div>
            <div className="mx-auto w-[300px]">
              <div className="relative rounded-[2.6rem] bg-ink-900 p-2.5 shadow-2xl">
                <div className="absolute top-2.5 left-1/2 -translate-x-1/2 w-24 h-[18px] bg-ink-900 rounded-b-2xl z-10" />
                <div className="rounded-[2.1rem] overflow-hidden bg-white h-[580px] overflow-y-auto">
                  <PagePreview title={title} bio={bio} avatarImage={avatarImage} avatarColor={avatarColor} blocks={blocks} socials={socials} design={design} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <AddBlockModal open={addOpen} onClose={() => setAddOpen(false)} onPick={addBlock} />
    </div>
  );
};

// ---- shared bits --------------------------------------------------------
const inputCls = "w-full px-3 h-10 bg-white border border-ink-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500";
const fieldCls = "w-full px-3 h-9 bg-white border border-ink-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500";
const labelCls = "block text-xs font-medium text-ink-700 mb-1.5";

const Card = ({ icon, title, subtitle, children }: any) => (
  <div className="bg-white border border-ink-200/70 rounded-2xl shadow-card overflow-hidden">
    <div className="flex items-center gap-2.5 px-5 pt-4 pb-3 border-b border-ink-100">
      <span className="w-8 h-8 rounded-lg bg-brand-50 text-brand-600 flex items-center justify-center shrink-0">{icon}</span>
      <div>
        <h3 className="text-sm font-semibold text-ink-900 leading-tight">{title}</h3>
        {subtitle && <p className="text-[11px] text-ink-500">{subtitle}</p>}
      </div>
    </div>
    <div className="p-5 space-y-4">{children}</div>
  </div>
);

const Field = ({ label, children }: any) => (
  <div>
    <label className={labelCls}>{label}</label>
    {children}
  </div>
);

const Segmented = ({ value, options, onChange }: { value: string; options: string[]; onChange: (v: any) => void }) => (
  <div className="inline-flex p-0.5 bg-ink-100 rounded-lg">
    {options.map((o) => (
      <button key={o} type="button" onClick={() => onChange(o)} className={`h-7 px-3 rounded-md text-xs font-medium capitalize transition-all ${value === o ? "bg-white shadow-sm text-ink-900" : "text-ink-500 hover:text-ink-900"}`}>{o}</button>
    ))}
  </div>
);

const ColorRow = ({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) => (
  <Field label={label}>
    <div className="flex items-center gap-2">
      <input type="color" value={value} onChange={(e) => onChange(e.target.value)} className="w-9 h-9 rounded-lg border border-ink-200 cursor-pointer bg-white p-0.5 shrink-0" />
      <input value={value} onChange={(e) => onChange(e.target.value)} className={`${fieldCls} font-mono`} />
    </div>
  </Field>
);

const toIsoOrNull = (v: string) => (v ? new Date(v).toISOString() : null);

const ScheduleRow = ({ block, onChange }: { block: PageBlock; onChange: (p: Partial<PageBlock>) => void }) => {
  const scheduled = !!(block.start_at || block.end_at);
  const [open, setOpen] = useState(scheduled);
  if (!open)
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 text-[11px] font-medium text-ink-500 hover:text-brand-600"
      >
        <CalendarClock size={12} /> Schedule visibility
      </button>
    );
  return (
    <div className="rounded-lg border border-ink-200 bg-ink-50/40 p-2.5 space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-semibold text-ink-600 flex items-center gap-1.5">
          <CalendarClock size={12} /> Visible window
        </span>
        <button
          type="button"
          onClick={() => { setOpen(false); onChange({ start_at: null, end_at: null }); }}
          className="text-[11px] text-ink-400 hover:text-rose-600"
        >
          Clear
        </button>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <label className="block">
          <span className="block text-[10px] text-ink-500 mb-1">Show from</span>
          <input
            type="datetime-local"
            value={toLocalInputValue(block.start_at)}
            onChange={(e) => onChange({ start_at: toIsoOrNull(e.target.value) })}
            className="w-full px-2 h-8 bg-white border border-ink-200 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500"
          />
        </label>
        <label className="block">
          <span className="block text-[10px] text-ink-500 mb-1">Hide after</span>
          <input
            type="datetime-local"
            value={toLocalInputValue(block.end_at)}
            onChange={(e) => onChange({ end_at: toIsoOrNull(e.target.value) })}
            className="w-full px-2 h-8 bg-white border border-ink-200 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500"
          />
        </label>
      </div>
      <p className="text-[10px] text-ink-400">Leave a field blank for no limit. Hidden blocks still show here in the editor.</p>
    </div>
  );
};

const BlockFields = ({
  block,
  onChange,
  customFields,
}: {
  block: PageBlock;
  onChange: (p: Partial<PageBlock>) => void;
  customFields: GhlCustomField[];
}) => {
  if (block.type === "divider")
    return <p className="text-xs text-ink-400">A thin divider line — no settings needed.</p>;

  if (block.type === "form")
    return (
      <div className="space-y-3">
        <input
          value={block.label}
          onChange={(e) => onChange({ label: e.target.value })}
          placeholder="Form heading (e.g. Get in touch)"
          className={fieldCls}
        />
        <input
          value={block.description}
          onChange={(e) => onChange({ description: e.target.value })}
          placeholder="Subtext (optional)"
          className={fieldCls}
        />
        <div>
          <div className="text-[11px] text-ink-600 mb-1.5 flex items-center">
            Fields to collect
            <HelpHint title="Form blocks" anchor="pages-forms">
              Collect leads straight into your CRM. Pick any contact field or
              custom field; every submission creates or updates a contact.
            </HelpHint>
          </div>
          <LeadFieldsEditor
            fields={block.fields || []}
            onChange={(fields) => onChange({ fields })}
            customFields={customFields}
          />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-[11px] text-ink-600 mb-1">Button label</label>
            <input
              value={block.button_label || ""}
              onChange={(e) => onChange({ button_label: e.target.value })}
              placeholder="Submit"
              className={fieldCls}
            />
          </div>
          <div>
            <label className="block text-[11px] text-ink-600 mb-1">After submit</label>
            <input
              value={block.success_message || ""}
              onChange={(e) => onChange({ success_message: e.target.value })}
              placeholder="Thanks! We'll be in touch."
              className={fieldCls}
            />
          </div>
        </div>
        <ScheduleRow block={block} onChange={onChange} />
      </div>
    );

  const typeFields =
    block.type === "header" ? (
      <input value={block.text} onChange={(e) => onChange({ text: e.target.value })} placeholder="Section heading" className={fieldCls} />
    ) : block.type === "text" ? (
      <textarea value={block.text} onChange={(e) => onChange({ text: e.target.value })} rows={3} placeholder="Your text…" className={`${fieldCls} h-auto py-2 resize-none`} />
    ) : block.type === "youtube" ? (
      <input value={block.url} onChange={(e) => onChange({ url: e.target.value })} placeholder="https://youtube.com/watch?v=…" className={`${fieldCls} font-mono`} />
    ) : block.type === "image" ? (
      <div className="space-y-2">
        <input value={block.image_url} onChange={(e) => onChange({ image_url: e.target.value })} placeholder="Image URL (https://…)" className={`${fieldCls} font-mono`} />
        <input value={block.url} onChange={(e) => onChange({ url: e.target.value })} placeholder="Link when clicked (optional)" className={`${fieldCls} font-mono`} />
      </div>
    ) : (
      <div className="space-y-2">
        <input value={block.label} onChange={(e) => onChange({ label: e.target.value })} placeholder="Button label" className={fieldCls} />
        <input value={block.url} onChange={(e) => onChange({ url: e.target.value })} placeholder="https://destination.com" className={`${fieldCls} font-mono`} />
        <input value={block.description} onChange={(e) => onChange({ description: e.target.value })} placeholder="Description (optional)" className={fieldCls} />
        <input value={block.image_url} onChange={(e) => onChange({ image_url: e.target.value })} placeholder="Thumbnail image URL (optional)" className={`${fieldCls} font-mono`} />
      </div>
    );

  return (
    <div className="space-y-3">
      {typeFields}

      {block.type === "link" && (
        <button
          type="button"
          onClick={() => onChange({ featured: !block.featured })}
          className={`inline-flex items-center gap-2 h-8 px-2.5 rounded-lg text-xs font-medium transition-colors ${
            block.featured ? "bg-amber-50 text-amber-700 ring-1 ring-amber-200" : "text-ink-500 hover:bg-ink-100"
          }`}
        >
          <Star size={13} fill={block.featured ? "currentColor" : "none"} />
          {block.featured ? "Featured" : "Make featured"}
        </button>
      )}

      <ScheduleRow block={block} onChange={onChange} />
    </div>
  );
};

const DesignControls = ({
  design: d, setD, socials, setSocials,
}: {
  design: PageDesign;
  setD: (p: Partial<PageDesign>) => void;
  socials: PageSocial[];
  setSocials: (s: PageSocial[]) => void;
}) => {
  const radius = d.button_shape === "pill" ? 999 : d.button_shape === "square" ? 6 : 14;
  const shadow = d.button_shadow === "none" ? "none" : d.button_shadow === "hard" ? "4px 4px 0 rgba(24,24,27,.85)" : `0 10px 24px -12px ${d.button_color}cc`;

  const hasSocial = (p: string) => socials.some((s) => s.platform === p);
  const toggleSocial = (p: string) =>
    setSocials(hasSocial(p) ? socials.filter((s) => s.platform !== p) : [...socials, { platform: p, url: "" }]);
  const setSocialUrl = (p: string, url: string) =>
    setSocials(socials.map((s) => (s.platform === p ? { ...s, url } : s)));
  const removeSocial = (p: string) => setSocials(socials.filter((s) => s.platform !== p));
  const meta = (p: string) => SOCIAL_PLATFORMS.find((x) => x.key === p);

  return (
    <>
      <Card icon={<Palette size={14} />} title="Theme" subtitle="One-click color presets">
        <div className="flex flex-wrap gap-3">
          {THEMES.map((t) => {
            const active = d.button_color === (t.patch.button_color || "") && d.bg_color === (t.patch.bg_color || d.bg_color);
            return (
              <button key={t.key} type="button" onClick={() => setD(t.patch)} title={t.key}
                className={`relative w-10 h-10 rounded-full ring-2 transition-transform hover:scale-110 ${active ? "ring-ink-900" : "ring-ink-200"}`}
                style={{ background: t.swatch }}>
                {active && <Check size={14} className="absolute inset-0 m-auto text-white drop-shadow" />}
              </button>
            );
          })}
        </div>
      </Card>

      <Card icon={<Sparkles size={14} />} title="Background & text">
        <Field label="Background"><Segmented value={d.bg_type} options={["color", "gradient"]} onChange={(v) => setD({ bg_type: v })} /></Field>
        {d.bg_type === "color" ? (
          <ColorRow label="Color" value={d.bg_color} onChange={(v) => setD({ bg_color: v })} />
        ) : (
          <div className="grid grid-cols-2 gap-3">
            <ColorRow label="From" value={d.bg_gradient_from} onChange={(v) => setD({ bg_gradient_from: v })} />
            <ColorRow label="To" value={d.bg_gradient_to} onChange={(v) => setD({ bg_gradient_to: v })} />
          </div>
        )}
        <ColorRow label="Text color" value={d.text_color} onChange={(v) => setD({ text_color: v })} />
        <Field label="Font">
          <select value={d.font} onChange={(e) => setD({ font: e.target.value })} className={inputCls} style={{ fontFamily: FONT_STACK[d.font] }}>
            {FONTS.map((f) => <option key={f} value={f}>{f}</option>)}
          </select>
        </Field>
      </Card>

      <Card icon={<SquareStack size={14} />} title="Buttons">
        <Field label="Shape"><Segmented value={d.button_shape} options={["rounded", "pill", "square"]} onChange={(v) => setD({ button_shape: v })} /></Field>
        <Field label="Shadow"><Segmented value={d.button_shadow} options={["none", "soft", "hard"]} onChange={(v) => setD({ button_shadow: v })} /></Field>
        <div className="grid grid-cols-2 gap-3">
          <ColorRow label="Button color" value={d.button_color} onChange={(v) => setD({ button_color: v })} />
          <ColorRow label="Button text" value={d.button_text_color} onChange={(v) => setD({ button_text_color: v })} />
        </div>
        <div className="pt-1">
          <div className="text-[11px] text-ink-400 mb-1.5">Preview</div>
          <div className="px-4 py-3 text-sm font-semibold text-center" style={{ background: d.button_color, color: d.button_text_color, borderRadius: radius, boxShadow: shadow }}>
            Sample button
          </div>
        </div>
      </Card>

      <Card icon={<Share2 size={14} />} title="Socials" subtitle="Pick icons, then add their links">
        <Field label="Select your icons">
          <div className="flex flex-wrap gap-2">
            {SOCIAL_PLATFORMS.map((p) => {
              const on = hasSocial(p.key);
              return (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => toggleSocial(p.key)}
                  className={`inline-flex items-center gap-1.5 h-8 pl-2 pr-3 rounded-full border text-xs font-medium transition-colors ${
                    on ? "bg-brand-50 border-brand-300 text-brand-700" : "bg-white border-ink-200 text-ink-600 hover:border-ink-300"
                  }`}
                >
                  <span className={on ? "text-brand-600" : "text-ink-400"}><SocialGlyph platform={p.key} size={15} /></span>
                  {p.label}
                </button>
              );
            })}
          </div>
        </Field>

        {socials.length > 0 && (
          <div className="space-y-2 pt-1">
            {socials.map((s) => (
              <div key={s.platform} className="flex items-center gap-2">
                <span className="w-9 h-9 rounded-lg bg-ink-100 text-ink-600 flex items-center justify-center shrink-0">
                  <SocialGlyph platform={s.platform} size={16} />
                </span>
                <input
                  value={s.url}
                  onChange={(e) => setSocialUrl(s.platform, e.target.value)}
                  placeholder={meta(s.platform)?.placeholder || "https://…"}
                  className={`${fieldCls} font-mono`}
                />
                <button type="button" onClick={() => removeSocial(s.platform)} className="w-8 h-8 rounded-lg text-ink-400 hover:bg-rose-50 hover:text-rose-600 flex items-center justify-center shrink-0"><X size={14} /></button>
              </div>
            ))}
          </div>
        )}

        <Field label="Placement"><Segmented value={d.social_placement} options={["top", "bottom"]} onChange={(v) => setD({ social_placement: v })} /></Field>
      </Card>
    </>
  );
};

export default Builder;
